import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import ModalV2 from 'features/shared/ModalV2';
import LoginForm from './LoginForm';

type LoginModalContextValue = {
  openLoginModal: () => void;
  closeLoginModal: () => void;
};

const LoginModalContext = createContext<LoginModalContextValue>({
  openLoginModal: () => {},
  closeLoginModal: () => {},
});

export const useLoginModal = () => useContext(LoginModalContext);

const LoginModalContent = styled.div`
  width: 100%;
  max-width: 484px;
  padding: 40px 48px 32px;
  box-sizing: border-box;
`;

export const LoginModalProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation<{ openLogin?: boolean }>();
  const history = useHistory();

  useEffect(() => {
    if (location.state?.openLogin) {
      setIsOpen(true);
      
      const { openLogin, ...restState } = location.state;
      
      history.replace({
        ...location,
        state: Object.keys(restState).length ? restState : undefined,
      });
    }
  }, [history, location]);

  const value = useMemo(() => ({
    openLoginModal: () => setIsOpen(true),
    closeLoginModal: () => setIsOpen(false),
  }), []);

  return (
    <LoginModalContext.Provider value={value}>
      {children}
      {isOpen && (
        <ModalV2 onClose={value.closeLoginModal} withoutPadding>
          <LoginModalContent data-testid="login-modal">
            <LoginForm />
          </LoginModalContent>
        </ModalV2>
      )}
    </LoginModalContext.Provider>
  );
};